import { isGetal } from '../format';
import type { Reden } from '../work-quality.api';

/**
 * De meetwaarde-cel: de atomaire eenheid van deze module.
 *
 * Zij toont nooit een kaal getal en nooit een leeg vak. Drie toestanden en geen vierde: gemeten,
 * te weinig gegevens, in bespreking. Waarom dat de betekenis zelf is en geen formaliteit staat in
 * `dqs-service/_bmad-output/planning-artifacts/qa-inzicht-portaal/EXPERIENCE.md`, *Accessibility
 * Floor*: kleur alléén leest als goed of slecht, en dit onderdeel gaat over mensen. De vorm staat
 * in de `DESIGN.md` daarnaast, *Components*.
 */

/**
 * De reden die het antwoord meestuurt, met haar eigen woorden.
 *
 * AD-19 is uitdrukkelijk: één toestand, één reden. `geen_beoordeeld_werk` is iets anders dan
 * `te_weinig_gegevens` — de eerste zegt dat niemand anders keek, de tweede dat er te weinig is om
 * een cijfer op te bouwen — en dezelfde persoon mag daar niet afhankelijk van de route twee
 * verschillende verklaringen voor krijgen. Een reden die deze weergave niet kent krijgt het
 * neutrale "niet gemeten" in plaats van stilzwijgend het label van een andere toestand.
 */
export function redenSleutel(reden: Reden | undefined): string {
  if (reden === 'te_weinig_gegevens') return 'qa.eigen.teWeinigGegevens';
  if (reden === 'geen_beoordeeld_werk') return 'qa.eigen.geenBeoordeeldWerk';
  if (reden === 'in_bespreking') return 'qa.eigen.inBespreking';
  return 'qa.eigen.nietGemeten';
}

/**
 * "Hier is niets gemeten" — het DS-patroon *geen procesdata bekend*: gedempt vlak, gestreepte rand,
 * `help`-icoon én label. De gestreepte rand is in deze module gereserveerd voor precies deze
 * betekenis.
 *
 * Bewust niet `StatusBadge status="process-no-data"` uit `@xxtract/ui`: die tekent dezelfde vorm
 * maar met een eigen SVG in plaats van het `help`-symbool dat zowel het design system als
 * acceptatiecriterium 7 van deze story bij naam noemen. De badge overnemen zou die afwijking
 * meenemen naar een scherm waar het icoon een letterlijke eis is.
 */
export function GeenMeting({ label }: { label: string }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-[var(--border)] bg-[var(--background-muted)] px-2 py-0.5 text-[11px] font-medium text-[var(--foreground-muted)]"
      data-werkkwaliteit-status="geen-meting"
    >
      <span aria-hidden="true" className="material-symbols-outlined text-[14px] leading-none">
        help
      </span>
      {label}
    </span>
  );
}

/** True zolang een percentage een percentage is. Buiten 0–100 is het een meetfout, geen waarde. */
export function isBruikbaarPercentage(waarde: unknown): waarde is number {
  return isGetal(waarde) && waarde >= 0 && waarde <= 100;
}
